/**
 * Detection of browser network-failure messages from failed fetch/XHR
 * calls. Shared by the Sentry init sampling filter (main.js) and by
 * fire-and-forget request error handling that must not report expected
 * connectivity failures.
 *
 * Each engine produces its own TypeError message for a request that
 * fails at the network layer (offline, DNS failure, connection reset,
 * blocked by an extension):
 * - Chromium: "Failed to fetch"
 * - WebKit:   "Load failed"
 * - Gecko:    "NetworkError when attempting to fetch resource."
 *
 * Prefix matching (not equality) also covers decorated variants such
 * as "Load failed (ipstatic.net)".
 */
const NETWORK_ERROR_MESSAGE_PREFIXES = [
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource',
];

const ABORT_MESSAGE_PREFIXES = [
    'AbortError',
    'The operation was aborted',
    'The user aborted a request',
    'signal is aborted without reason',
];

/**
 * @param {unknown} message Error message (or Sentry exception value)
 * @returns {boolean} true when the message is a network-layer failure
 */
export function isNetworkErrorMessage(message) {
    // A rethrown error stringified into a new one carries its type as a prefix.
    const text = typeof message === 'string' ? message.replace(/^TypeError:\s*/, '') : '';
    return NETWORK_ERROR_MESSAGE_PREFIXES.some((prefix) => text.startsWith(prefix));
}

/**
 * An AbortError, or a message naming an aborted request. This covers a
 * fetch cancelled by navigation or a caller timeout, and also media
 * play() calls interrupted by pause().
 *
 * @param {unknown} type Error name (or Sentry exception type)
 * @param {unknown} message Error message (or Sentry exception value)
 * @returns {boolean} true when the error is an abort
 */
export function isAbortError(type, message) {
    if (type === 'AbortError') {
        return true;
    }
    const text = typeof message === 'string' ? message : '';
    return ABORT_MESSAGE_PREFIXES.some((prefix) => text.startsWith(prefix));
}
